import React, { memo } from 'react';
import Animated, { Extrapolation, interpolate, useAnimatedStyle } from 'react-native-reanimated';

import { EventInfo } from '@/api/events/types/types';
import { EVENT_SHEET_TITLE_HEIGHT } from '@/components/event-sheet/EventSheetTitle';
import { Box } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { Handle, MODAL_HANDLE_HEIGHT } from '@/feature/alerts/components/Modal';
import { CircularPagerSheetHandleGestureWrapper } from '@/feature/circular-pager/BottomSheetCircularPagerWrapper';
import { useScrollableModalContext } from '@/feature/circular-pager/ScrollableModalContextProvider';
import { EVENT_HEADER_HERO_IMAGE_HEIGHT } from '@/feature/lobby/components/EventHeader';
import { MaxWidthWrapper } from '@/feature/responsive-design/WebComponents';
import { common, withOpacity } from '@/styles/styles';
import { League } from '@/types/api.generated';

import { useSinglePlayerProfile } from '../SinglePlayerProfileProvider';
import { FantasyEventSheetTitle } from './FantasyEventSheetTitle';

// TODO: manage UFC header if dedicated event not set -> easier to do in the PR with UFC fights tabs
export const EventDetailsSheetHandle = memo(
    ({
        eventDedicatedHeaderInfo,
        eventInfo,
    }: {
        eventInfo: EventInfo | null;
        eventDedicatedHeaderInfo: EventInfo | null;
    }) => {
        // we cannot compute shouldFreeze above and passed it down as props because it would flicker the modal
        // when we swipe through the tabs
        // so we will compute "shouldFreeze" here again using animated values
        const { playerProfileAnimatedIndex } = useSinglePlayerProfile();

        const { scrollOffset } = useScrollableModalContext();

        const backgroundStyle = useAnimatedStyle(() => {
            if (!eventDedicatedHeaderInfo) {
                return {
                    backgroundColor: ds.colorValue.background.neutral.base,
                };
            }
            return {
                backgroundColor:
                    scrollOffset.value > EVENT_HEADER_HERO_IMAGE_HEIGHT - MODAL_HANDLE_HEIGHT - EVENT_SHEET_TITLE_HEIGHT
                        ? ds.colorValue.background.neutral.base
                        : 'transparent',
            };
        });

        const handleStyle = useAnimatedStyle(() => ({
            opacity: interpolate(playerProfileAnimatedIndex.value, [-1, 0], [1, 0], Extrapolation.CLAMP),
        }));

        if (!eventInfo) {
            return null;
        }

        return (
            <CircularPagerSheetHandleGestureWrapper>
                <Box
                    position={'absolute'}
                    zIndex={1}
                    width={'100%'}
                    height={EVENT_SHEET_TITLE_HEIGHT + MODAL_HANDLE_HEIGHT}
                    overflow={'hidden'}
                >
                    <MaxWidthWrapper>
                        <Animated.View style={[common.flex, backgroundStyle]}>
                            <Animated.View style={handleStyle}>
                                <Handle
                                    handleColor={withOpacity(ds.colorValue.background.neutral.inverse, 0.15)}
                                    handleContainerStyle={common.backgroundColorTransparent}
                                />
                            </Animated.View>
                            <FantasyEventSheetTitle
                                event={eventInfo}
                                isOnlyDedicatedHeader={
                                    eventDedicatedHeaderInfo !== null || eventInfo.league === League.Ufc
                                }
                                scrollOffset={scrollOffset}
                            />
                        </Animated.View>
                    </MaxWidthWrapper>
                </Box>
            </CircularPagerSheetHandleGestureWrapper>
        );
    }
);

EventDetailsSheetHandle.displayName = 'EventDetailsSheetHandle';
